import * as THREE from 'three';
import { gsap } from 'gsap';
import { stageIntroInOut, toastInOut } from './ui/animator.js';
import { spawnRuntime } from './spawnRuntime.js';

const STAGE_SUBTITLES = {
  1: 'The Lobby',
  2: 'The BreakRoom',
  3: 'The The lounge',
  4: 'The MeetingRoom',
  5: 'The Awaking Supervisor',
  6: 'The Conffrence Room',
  7: 'The HR',
  8: 'The Manager',
  9: 'who can’t be bothered',
  10: 'The Top',
};

const WEAPON_ICON_PATHS = Object.freeze({
  shinkansen_pistol: '/assets/2D/IconWeapon/Pistol/Pistol_Horizontal_01.png',
  reiketsu_shotgun: '/assets/2D/IconWeapon/Shotgun/Shotgun_Horizontal_01.png',
  jinsoku_smg: '/assets/2D/IconWeapon/Uzi/Uzi_Horizontal_01.png',
  stormcaller_smr2: '/assets/2D/IconWeapon/Rifle/Rifle_Horizontal_01.png',
  tenrai_mortar: '/assets/2D/IconWeapon/Mortar/Mortar_Horizontal_01.png',
  jigoku_launcher: '/assets/2D/IconWeapon/AssaultRifle/AssaultRifle_Horizontal_01.png',
});
const MEDKIT_ICON_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96">
    <g fill="none" stroke="#ff2d6b" stroke-width="4" stroke-linejoin="round">
      <path d="M24 34h48v34H24z"/>
      <path d="M34 34v-6h28v6"/>
    </g>
    <path fill="#ff2d6b" d="M44 44h8v8h8v8h-8v8h-8v-8h-8v-8h8z"/>
  </svg>`
);
const MEDKIT_ICON_PATH = `data:image/svg+xml;utf8,${MEDKIT_ICON_SVG}`;

export class HUD {
  constructor() {
    this.el = document.getElementById('hud') || document.body.appendChild(Object.assign(document.createElement('div'), { id: 'hud' }));
    this._legacyTop = this.el.querySelector('.hud-top');
    this._legacyBottom = this.el.querySelector('.hud-bottom');
    if (this._legacyTop) this._legacyTop.style.display = 'none';
    if (this._legacyBottom) this._legacyBottom.style.display = 'none';

    this.toastEl = document.getElementById('toast');
    this.stageIntroEl = document.getElementById('stage-intro');
    this.stageIntroMain = document.getElementById('stage-intro-main');
    this.stageIntroSub = document.getElementById('stage-intro-sub');
    this.dropTooltipEl = document.getElementById('drop-tooltip');
    this.dropTooltipName = document.getElementById('drop-tooltip-name');
    this.dropTooltipMeta = document.getElementById('drop-tooltip-meta');
    this.dropTooltipHint = document.getElementById('drop-tooltip-hint');
    this.objectiveBanner = document.getElementById('objective-banner');
    this.objectiveText = document.getElementById('objective-text');
    this.objectiveHint = document.getElementById('objective-hint');
    this.objectiveProgressPercent = document.getElementById('objective-progress-percent');
    this.objectiveProgressCount = document.getElementById('objective-progress-count');
    this.objectiveProgressFill = document.getElementById('objective-progress-fill');
    this._dropTooltipRarityClass = '';
    this._toastTween = null;
    this._objectiveHideTimer = null;
    this._objectiveCompactTimer = null;
    this._comboDisplayValue = 'grid';
    this._lastComboCount = 0;
    this._reloadDuration = 0;
    this._dimmed = false;
    this._medkitFxTimer = null;
    this._exitOpenNoticeTimer = null;
    this._objectiveCompleteNoticeShown = false;

    this._buildTacticalHud();

    this.exitIndicator = document.createElement('div');
    this.exitIndicator.className = 'exit-indicator hidden';
    this.exitIndicator.innerHTML = '<span class="exit-indicator-arrow"></span><span class="exit-indicator-label">EXIT</span>';
    this.el.appendChild(this.exitIndicator);
    this.exitOpenNotice = document.createElement('div');
    this.exitOpenNotice.className = 'hud-exit-open-notice hidden';
    this.exitOpenNotice.textContent = 'EXIT DOOR OPEN';
    this.el.appendChild(this.exitOpenNotice);
    this._dangerVignette = document.getElementById('danger-vignette');
    this._indicatorVec = new THREE.Vector3();
    this._pickupPromptEl = null;
    this._pickupPromptNameEl = null;
    this._pickupPromptDpsEl = null;
    this._pickupPromptHintEl = null;
    this._pickupPromptTimerFillEl = null;
    this._crosshairEl = null;
    this._buildPickupPrompt();
  }

  _buildTacticalHud() {
    const existing = this.el.querySelector('.tactical-hud-root');
    if (existing) existing.remove();
    this.el.insertAdjacentHTML('beforeend', `
      <div class="tactical-hud-root" aria-hidden="false">
        <div class="hud-vitals" data-hud="vitals">
          <div class="hud-vitals-label">VITALS</div>
          <div class="hud-hp-bar">
            <div class="hud-hp-fill"></div>
            <div class="hud-hp-text">0 / 0</div>
          </div>
          <div class="hud-stamina-bar"><div class="hud-stamina-fill"></div></div>
        </div>
        <div class="hud-item-slot" data-hud="item">
          <div class="hud-item-hex">
            <img class="hud-item-icon" alt="" />
            <div class="hud-item-empty">+</div>
            <div class="hud-item-badge">0</div>
          </div>
          <div class="hud-item-label">MEDKIT</div>
        </div>
        <div class="hud-weapon-panel" data-hud="weapon">
          <div class="hud-weapon-main">
            <div class="hud-weapon-left">
              <img class="hud-weapon-icon" alt="" />
              <div class="hud-weapon-name">UNARMED</div>
            </div>
            <div class="hud-weapon-center">
              <div class="hud-weapon-ammo">0</div>
              <div class="hud-weapon-reserve">/0</div>
            </div>
            <div class="hud-weapon-right">
              <div class="hud-weapon-shield"></div>
              <div class="hud-weapon-ammo-type"></div>
            </div>
          </div>
          <div class="hud-reload-track"><div class="hud-reload-fill"></div></div>
        </div>
        <div class="hud-combo-panel" data-hud="combo">
          <div class="hud-combo-label">COMBO</div>
          <div class="hud-combo-value">0x</div>
          <div class="hud-combo-kills">0 KILLS</div>
        </div>
        <div class="hud-killquota-panel" data-hud="killquota">
          <div class="hud-killquota-text">KILLS: 0 / 0</div>
        </div>
        <div class="hud-crosshair" aria-hidden="true">
          <span class="hud-crosshair-dot"></span>
        </div>
      </div>
    `);
    this.root = this.el.querySelector('.tactical-hud-root');
    this.hpFill = this.root.querySelector('.hud-hp-fill');
    this.hpText = this.root.querySelector('.hud-hp-text');
    this.staminaFill = this.root.querySelector('.hud-stamina-fill');
    this.itemSlot = this.root.querySelector('.hud-item-slot');
    this.itemIcon = this.root.querySelector('.hud-item-icon');
    this.itemBadge = this.root.querySelector('.hud-item-badge');
    this.weaponPanel = this.root.querySelector('.hud-weapon-panel');
    this.weaponIcon = this.root.querySelector('.hud-weapon-icon');
    this.weaponName = this.root.querySelector('.hud-weapon-name');
    this.weaponAmmo = this.root.querySelector('.hud-weapon-ammo');
    this.weaponReserve = this.root.querySelector('.hud-weapon-reserve');
    this.weaponAmmoType = this.root.querySelector('.hud-weapon-ammo-type');
    this.weaponShield = this.root.querySelector('.hud-weapon-shield');
    this.reloadTrack = this.root.querySelector('.hud-reload-track');
    this.reloadFill = this.root.querySelector('.hud-reload-fill');
    this.comboPanel = this.root.querySelector('.hud-combo-panel');
    this.comboValue = this.root.querySelector('.hud-combo-value');
    this.comboKills = this.root.querySelector('.hud-combo-kills');
    if (this.comboPanel) {
      const displayValue = window.getComputedStyle(this.comboPanel).display;
      this._comboDisplayValue = displayValue && displayValue !== 'none' ? displayValue : 'grid';
      this.comboPanel.style.display = 'none';
      this.comboPanel.setAttribute('aria-hidden', 'true');
    }
    this.killQuotaPanel = this.root.querySelector('.hud-killquota-panel');
    this.killQuotaText = this.root.querySelector('.hud-killquota-text');
    this._crosshairEl = this.root.querySelector('.hud-crosshair');
  }

  show() { this.el.classList.remove('hidden'); }
  hide() {
    this.el.classList.add('hidden');
    this.exitIndicator?.classList.add('hidden');
    this.exitOpenNotice?.classList.add('hidden');
    this.roomTimer?.classList.add('hidden');
    if (this._exitOpenNoticeTimer) {
      clearTimeout(this._exitOpenNoticeTimer);
      this._exitOpenNoticeTimer = null;
    }
    if (this._objectiveHideTimer) {
      clearTimeout(this._objectiveHideTimer);
      this._objectiveHideTimer = null;
    }
    if (this._objectiveCompactTimer) {
      clearTimeout(this._objectiveCompactTimer);
      this._objectiveCompactTimer = null;
    }
    this.hideDropTooltip();
    this.hidePickupPrompt();
  }

  _buildPickupPrompt() {
    const existing = document.getElementById('pickup-prompt');
    if (existing) existing.remove();
    const root = document.createElement('div');
    root.id = 'pickup-prompt';
    root.className = 'pickup-prompt hidden';
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML = `
      <div class="pickup-prompt-name"></div>
      <div class="pickup-prompt-dps"></div>
      <div class="pickup-prompt-hint">[E] Equip &nbsp;&nbsp; [F] Discard</div>
      <div class="pickup-prompt-timer"><div class="pickup-prompt-timer-fill"></div></div>
    `;
    this.el.appendChild(root);
    this._pickupPromptEl = root;
    this._pickupPromptNameEl = root.querySelector('.pickup-prompt-name');
    this._pickupPromptDpsEl = root.querySelector('.pickup-prompt-dps');
    this._pickupPromptHintEl = root.querySelector('.pickup-prompt-hint');
    this._pickupPromptTimerFillEl = root.querySelector('.pickup-prompt-timer-fill');
  }

  renderPickupPrompt(itemName, currentDPS, newDPS, timerPercent) {
    if (!this._pickupPromptEl) return;
    const current = Math.max(0, Number(currentDPS) || 0);
    const next = Math.max(0, Number(newDPS) || 0);
    const diff = next - current;
    const sign = diff > 0 ? '+' : '';
    const pct = Math.max(0, Math.min(1, Number(timerPercent) || 0));

    this._pickupPromptNameEl.textContent = String(itemName || 'Unknown Weapon').toUpperCase();
    this._pickupPromptDpsEl.textContent = `DPS ${next.toFixed(1)} (${sign}${diff.toFixed(1)})`;
    this._pickupPromptDpsEl.classList.toggle('is-better', diff >= 0);
    this._pickupPromptDpsEl.classList.toggle('is-worse', diff < 0);

    if (this._pickupPromptTimerFillEl) {
      this._pickupPromptTimerFillEl.style.width = `${(pct * 100).toFixed(1)}%`;
      const red = Math.floor(255 * (1 - pct));
      const green = Math.floor(255 * pct);
      this._pickupPromptTimerFillEl.style.background = `rgb(255, ${Math.max(40, green)}, ${Math.max(40, green - red * 0.2)})`;
    }
    this._pickupPromptEl.classList.remove('hidden');
    this._pickupPromptEl.setAttribute('aria-hidden', 'false');
  }

  hidePickupPrompt() {
    if (!this._pickupPromptEl) return;
    this._pickupPromptEl.classList.add('hidden');
    this._pickupPromptEl.setAttribute('aria-hidden', 'true');
  }

  setDimmed(dimmed) {
    this._dimmed = !!dimmed;
    this.root?.classList.toggle('is-dimmed', this._dimmed);
  }

  updateCrosshair(screenX, screenY) {
    if (!this._crosshairEl || !this._crosshairEl.isConnected) {
      this._crosshairEl = document.querySelector('#hud .hud-crosshair');
    }
    if (!this._crosshairEl) return;
    const x = Number.isFinite(screenX) ? screenX : window.innerWidth * 0.5;
    const y = Number.isFinite(screenY) ? screenY : window.innerHeight * 0.5;
    const clampedX = Math.max(0, Math.min(window.innerWidth, x));
    const clampedY = Math.max(0, Math.min(window.innerHeight, y));
    this._crosshairEl.style.left = `${clampedX}px`;
    this._crosshairEl.style.top = `${clampedY}px`;
  }

  update(player, roomIndex, totalRooms) {
    this.setHP(player.hp, player.maxHp);
    const staminaMax = Number.isFinite(player.maxStamina) ? player.maxStamina : 1;
    this.setStamina(player.stamina ?? staminaMax, staminaMax);

    const preferredHand = player.getPreferredHudHand?.() || 'right';
    const weaponState = player.getHandHudState?.(preferredHand)
      || player.getHandHudState?.('right')
      || player.getHandHudState?.('left');
    if (weaponState) {
      this.setWeapon(
        weaponState.weaponName,
        weaponState.ammoInMag ?? 0,
        weaponState.ammoReserve ?? 0,
        WEAPON_ICON_PATHS[weaponState.weaponId] || ''
      );
      this.weaponAmmoType.textContent = weaponState.weaponName?.toUpperCase() || '';
      const handState = player.getHandState?.(preferredHand) || null;
      const duration = Number.isFinite(handState?.reloadDuration) ? handState.reloadDuration : 0;
      const remaining = Number.isFinite(handState?.reloadTime) ? handState.reloadTime : 0;
      this.setReloading(!!weaponState.isReloading, duration, remaining);
      const ammoRatio = (Number(weaponState.ammoInMag) || 0) / Math.max(1, Number(handState?.magCapacity) || 1);
      const shieldState = ammoRatio >= 0.66 ? 'full' : ammoRatio > 0.25 ? 'partial' : 'empty';
      this.weaponShield.dataset.state = shieldState;
    }

    const medkits = Math.max(0, Number(player.medkits) || 0);
    this.setItem(MEDKIT_ICON_PATH, medkits);

    if (this._dangerVignette) {
      const hpRatio = Math.max(0, Math.min(1, (player.hp || 0) / Math.max(1, player.maxHp || 1)));
      this._dangerVignette.classList.toggle('danger-critical', hpRatio <= 0.1);
      this._dangerVignette.classList.toggle('danger-low', hpRatio > 0.1 && hpRatio <= 0.25);
    }

    this.updateKillQuota(spawnRuntime.getKillCount(), spawnRuntime.getQuota(), spawnRuntime.isRoomComplete());
  }

  updateKillQuota(killCount = 0, quota = 0, isQuotaMet = false) {
    if (!this.killQuotaText || !this.killQuotaPanel) return;
    const kills = Math.max(0, Number(killCount) || 0);
    const target = Math.max(0, Number(quota) || 0);
    const wasExitOpen = this.killQuotaPanel.classList.contains('is-exit-open');
    if (isQuotaMet && target > 0) {
      if (!wasExitOpen) {
        this.killQuotaPanel.classList.remove('exit-open-pulse');
        void this.killQuotaPanel.offsetWidth;
        this.killQuotaPanel.classList.add('exit-open-pulse');
      }
      this.killQuotaPanel.classList.add('is-exit-open');
      this.killQuotaText.textContent = 'EXIT OPEN';
      return;
    }
    this.killQuotaPanel.classList.remove('is-exit-open', 'exit-open-pulse');
    this.killQuotaText.textContent = `KILLS: ${kills} / ${target}`;
  }

  showExitOpenNotice(message = 'EXIT DOOR OPEN', durationMs = 1800) {
    if (!this.exitOpenNotice) return;
    this.exitOpenNotice.textContent = message;
    this.exitOpenNotice.classList.remove('hidden', 'is-visible');
    void this.exitOpenNotice.offsetWidth;
    this.exitOpenNotice.classList.add('is-visible');
    if (this._exitOpenNoticeTimer) clearTimeout(this._exitOpenNoticeTimer);
    this._exitOpenNoticeTimer = setTimeout(() => {
      this.exitOpenNotice?.classList.add('hidden');
      this.exitOpenNotice?.classList.remove('is-visible');
      this._exitOpenNoticeTimer = null;
    }, Math.max(300, Number(durationMs) || 1800));
  }

  setHP(current, max) {
    const safeMax = Math.max(1, Number(max) || 1);
    const safeCurrent = Math.max(0, Math.min(safeMax, Number(current) || 0));
    const ratio = safeCurrent / safeMax;
    this.hpFill.style.width = `${(ratio * 100).toFixed(1)}%`;
    this.hpText.textContent = `${safeCurrent} / ${safeMax}`;
    this.hpFill.classList.toggle('is-warning', ratio < 0.5 && ratio >= 0.25);
    this.hpFill.classList.toggle('is-critical', ratio < 0.25);
  }

  setStamina(current, max) {
    const safeMax = Math.max(1, Number(max) || 1);
    const safeCurrent = Math.max(0, Math.min(safeMax, Number(current) || 0));
    const ratio = safeCurrent / safeMax;
    this.staminaFill.style.width = `${(ratio * 100).toFixed(1)}%`;
  }

  setWeapon(name, ammoClip, ammoReserve, iconSrc = '') {
    const clip = Number.isFinite(ammoClip) ? Math.max(0, Math.floor(ammoClip)) : 0;
    const reserve = Number.isFinite(ammoReserve) ? Math.max(0, Math.floor(ammoReserve)) : 0;
    this.weaponName.textContent = String(name || 'UNARMED').toUpperCase();
    this.weaponAmmo.textContent = String(clip);
    this.weaponReserve.textContent = `/${reserve}`;
    if (iconSrc) {
      this.weaponIcon.src = iconSrc;
      this.weaponIcon.style.visibility = 'visible';
    } else {
      this.weaponIcon.removeAttribute('src');
      this.weaponIcon.style.visibility = 'hidden';
    }
  }

  setReloading(isReloading, duration = 0, remaining = null) {
    const active = !!isReloading;
    this.weaponPanel.classList.toggle('is-reloading', active);
    if (!active) {
      this.reloadFill.style.width = '0%';
      return;
    }
    this._reloadDuration = Math.max(0.01, Number(duration) || 0.01);
    if (Number.isFinite(remaining)) {
      const elapsed = Math.max(0, this._reloadDuration - Math.max(0, Number(remaining) || 0));
      const ratio = Math.max(0, Math.min(1, elapsed / this._reloadDuration));
      this.reloadFill.style.width = `${(ratio * 100).toFixed(1)}%`;
    } else {
      this.reloadFill.style.width = '100%';
    }
  }

  setItem(iconSrc = '', quantity = 0) {
    const qty = Number.isFinite(quantity) ? Math.max(0, Math.floor(quantity)) : 0;
    const hasItem = !!iconSrc && qty > 0;
    this.itemSlot.classList.toggle('is-empty', !hasItem);
    if (iconSrc) {
      this.itemIcon.src = iconSrc;
      this.itemIcon.style.visibility = 'visible';
    } else {
      this.itemIcon.removeAttribute('src');
      this.itemIcon.style.visibility = 'hidden';
    }
    this.itemBadge.textContent = String(qty);
    this.itemBadge.style.display = hasItem ? 'grid' : 'none';
  }

  playMedkitUseFx() {
    if (!this.itemSlot) return;
    if (this._medkitFxTimer) {
      clearTimeout(this._medkitFxTimer);
      this._medkitFxTimer = null;
    }
    this.itemSlot.classList.remove('medkit-use-success');
    void this.itemSlot.offsetWidth;
    this.itemSlot.classList.add('medkit-use-success');
    this._medkitFxTimer = setTimeout(() => {
      this.itemSlot?.classList.remove('medkit-use-success');
      this._medkitFxTimer = null;
    }, 340);
  }

  updateRoomTimer(seconds = 0, visible = true) {
    if (!this.roomTimer || !this.roomTimerText) return;
    this.roomTimerText.textContent = formatRoomTimer(seconds);
    this.roomTimer.classList.toggle('hidden', !visible);
    this.roomTimer.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  updateCombo(combo = 0) {
    const safeCombo = Number.isFinite(combo) ? Math.max(0, Math.floor(combo)) : 0;
    if (!this.comboPanel || !this.comboValue || !this.comboKills) return;
    if (safeCombo === 0) {
      gsap.killTweensOf(this.comboPanel);
      gsap.set(this.comboPanel, { scale: 1, rotation: 0 });
      this.comboPanel.style.display = 'none';
      this.comboPanel.setAttribute('aria-hidden', 'true');
      this.comboValue.textContent = '0x';
      this.comboKills.textContent = '0 KILLS';
      this._lastComboCount = 0;
      return;
    }
    this.comboPanel.style.display = this._comboDisplayValue || 'grid';
    this.comboPanel.setAttribute('aria-hidden', 'false');
    this.comboValue.textContent = `${safeCombo}x`;
    this.comboKills.textContent = `${safeCombo} KILLS`;
    if (safeCombo > this._lastComboCount) {
      gsap.killTweensOf(this.comboPanel);
      gsap.timeline({ defaults: { overwrite: 'auto' } })
        .set(this.comboPanel, { scale: 1.38, rotation: -8 })
        .to(this.comboPanel, { scale: 1.38, rotation: -8, duration: 0.09, ease: 'none' })
        .to(this.comboPanel, { scale: 1, rotation: 0, duration: 0.26, ease: 'back.out(2.4)' });
    }
    this._lastComboCount = safeCombo;
  }

  setFragments(_ids) {
    // Legacy hook kept intentionally for runtime compatibility.
  }

  toast(msg, ms = 1400) {
    if (!this.toastEl) return;
    this.toastEl.textContent = msg;
    this._toastTween?.kill?.();
    this._toastTween = toastInOut(this.toastEl, ms);
  }

  showRoomClearBonus() {
    this.toast('Room Cleared - Bonus Dropped!', 2500);
  }

  showObjectiveUpdate(message, hint = '') {
    if (!this.objectiveBanner || !this.objectiveText) return;
    const text = String(message || '');
    this.objectiveText.textContent = text;
    if (this.objectiveHint && hint) this.objectiveHint.textContent = hint;
    this.objectiveBanner.classList.remove('hidden');
    this.objectiveBanner.setAttribute('aria-hidden', 'false');
    if (/objective\s*complete/i.test(text) && !this._objectiveCompleteNoticeShown) {
      this._objectiveCompleteNoticeShown = true;
      this.showExitOpenNotice('EXIT DOOR OPEN');
    }
  }

  setObjectiveProgress({ text = '', hint = '', current = 0, target = 0, show = true } = {}) {
    if (!this.objectiveBanner || !this.objectiveText) return;
    if (text) this.objectiveText.textContent = text;
    if (this.objectiveHint && hint) this.objectiveHint.textContent = hint;
    const safeTarget = Math.max(1, Number(target) || 1);
    const safeCurrent = Math.max(0, Math.min(safeTarget, Number(current) || 0));
    const pct = Math.round((safeCurrent / safeTarget) * 100);
    if (this.objectiveProgressPercent) this.objectiveProgressPercent.textContent = `${pct}%`;
    if (this.objectiveProgressCount) this.objectiveProgressCount.textContent = `${safeCurrent} / ${safeTarget}`;
    if (this.objectiveProgressFill) this.objectiveProgressFill.style.width = `${pct}%`;
    this.objectiveBanner.classList.toggle('hidden', !show);
    this.objectiveBanner.setAttribute('aria-hidden', show ? 'false' : 'true');
    const isObjectiveComplete = show && safeTarget > 0 && safeCurrent >= safeTarget;
    if (!isObjectiveComplete || !show) {
      this._objectiveCompleteNoticeShown = false;
      return;
    }
    if (!this._objectiveCompleteNoticeShown) {
      this._objectiveCompleteNoticeShown = true;
      this.showExitOpenNotice('EXIT DOOR OPEN');
    }
  }

  showStageIntro(stageNum) {
    if (!this.stageIntroEl || !this.stageIntroMain || !this.stageIntroSub) return;
    const parsed = Number(stageNum);
    const safeStage = Number.isFinite(parsed) ? Math.max(1, Math.floor(parsed)) : 1;
    this.stageIntroMain.textContent = STAGE_SUBTITLES[safeStage] || 'The Depth';
    this.stageIntroSub.textContent = `DEPTH LEVEL .${String(safeStage).padStart(2, '0')}`;
    stageIntroInOut(this.stageIntroEl, this.stageIntroMain, this.stageIntroSub, { holdMs: 1150 });
  }

  showDropTooltip({ name = '', meta = '', hint = '', rarity = '' } = {}) {
    if (!this.dropTooltipEl) return;
    if (this._dropTooltipRarityClass) {
      this.dropTooltipEl.classList.remove(this._dropTooltipRarityClass);
      this._dropTooltipRarityClass = '';
    }
    const rarityClass = rarity ? `rarity-${rarity}` : '';
    if (rarityClass) {
      this.dropTooltipEl.classList.add(rarityClass);
      this._dropTooltipRarityClass = rarityClass;
    }
    if (this.dropTooltipName) this.dropTooltipName.textContent = name;
    if (this.dropTooltipMeta) this.dropTooltipMeta.textContent = meta;
    if (this.dropTooltipHint) this.dropTooltipHint.textContent = hint;
    this.dropTooltipEl.classList.remove('hidden');
    this.dropTooltipEl.classList.add('visible');
    this.dropTooltipEl.setAttribute('aria-hidden', 'false');
  }

  moveDropTooltip(screenX, screenY, viewportWidth = window.innerWidth, viewportHeight = window.innerHeight) {
    if (!this.dropTooltipEl || this.dropTooltipEl.classList.contains('hidden')) return;
    const margin = 14;
    const cursorOffsetX = 18;
    const cursorOffsetY = 16;
    const tooltipRect = this.dropTooltipEl.getBoundingClientRect();
    let x = screenX + cursorOffsetX;
    let y = screenY + cursorOffsetY;
    if (x + tooltipRect.width > viewportWidth - margin) x = Math.max(margin, screenX - tooltipRect.width - 12);
    if (y + tooltipRect.height > viewportHeight - margin) y = Math.max(margin, viewportHeight - tooltipRect.height - margin);
    this.dropTooltipEl.style.left = `${x}px`;
    this.dropTooltipEl.style.top = `${y}px`;
  }

  hideDropTooltip() {
    if (!this.dropTooltipEl) return;
    this.dropTooltipEl.classList.remove('visible');
    this.dropTooltipEl.classList.add('hidden');
    this.dropTooltipEl.setAttribute('aria-hidden', 'true');
    if (this._dropTooltipRarityClass) {
      this.dropTooltipEl.classList.remove(this._dropTooltipRarityClass);
      this._dropTooltipRarityClass = '';
    }
  }

  updateExitIndicator(room, camera) {
    if (!this.exitIndicator || !room?.doorOpen || !room?.door || !camera) {
      this.exitIndicator?.classList.add('hidden');
      return;
    }

    const doorY = room.door.mesh?.position?.y ?? 1.2;
    this._indicatorVec.set(room.door.x, doorY, room.door.z).project(camera);
    const zInFront = this._indicatorVec.z >= -1 && this._indicatorVec.z <= 1;
    const onScreen = zInFront
      && this._indicatorVec.x >= -1 && this._indicatorVec.x <= 1
      && this._indicatorVec.y >= -1 && this._indicatorVec.y <= 1;

    if (onScreen) {
      this.exitIndicator.classList.add('hidden');
      return;
    }

    let ndcX = this._indicatorVec.x;
    let ndcY = this._indicatorVec.y;
    if (this._indicatorVec.z > 1) {
      ndcX *= -1;
      ndcY *= -1;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const rawScreenX = (ndcX * 0.5 + 0.5) * width;
    const rawScreenY = (-ndcY * 0.5 + 0.5) * height;
    const margin = 36;
    const clampedX = Math.max(margin, Math.min(width - margin, rawScreenX));
    const clampedY = Math.max(margin, Math.min(height - margin, rawScreenY));
    const angle = Math.atan2(rawScreenY - height * 0.5, rawScreenX - width * 0.5);

    this.exitIndicator.style.left = `${clampedX}px`;
    this.exitIndicator.style.top = `${clampedY}px`;
    this.exitIndicator.style.transform = 'translate(-50%, -50%)';
    const arrow = this.exitIndicator.querySelector('.exit-indicator-arrow');
    if (arrow) arrow.style.transform = `rotate(${angle}rad)`;
    this.exitIndicator.classList.remove('hidden');
  }
}

function formatRoomTimer(seconds) {
  const safeSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const minutes = Math.floor(safeSeconds / 60);
  const wholeSeconds = Math.floor(safeSeconds % 60);
  const centiseconds = Math.floor((safeSeconds % 1) * 100);
  return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
}
